import { create } from 'zustand';
import { Conversation, Message, ConversationFilters, StatusCounts, ChatbotInfo, GlobalFilters, AgentOption, ConversationLabel } from '../types/liveChat.types';
import { liveChatService } from '../services/liveChatService';

const parseFilterValues = (value?: string) =>
  (value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

const resolveMessageReplies = (messages: Message[]): Message[] => {
  const messageMap = new Map<string, Message>();
  messages.forEach((msg) => {
    messageMap.set(msg._id, msg);
  });

  return messages.map((msg) => {
    if (msg.replyTo) {
      if (typeof msg.replyTo === 'string') {
        const parent = messageMap.get(msg.replyTo);
        if (parent) {
          return {
            ...msg,
            replyTo: parent,
          };
        }
      } else if (typeof msg.replyTo === 'object' && msg.replyTo._id) {
        const parent = messageMap.get(msg.replyTo._id);
        if (parent) {
          return {
            ...msg,
            replyTo: parent,
          };
        }
      }
    }
    return msg;
  });
};

const matchesGlobalSearch = (conversation: Conversation, search?: string) => {
  const query = search?.trim().toLowerCase();
  if (!query) return true;

  const haystacks = [
    conversation.metadata?.userName,
    conversation.metadata?.lastMessagePreview,
    conversation.userPhone,
    conversation.visitorId,
    conversation.instagramUserId,
    conversation.whatsappContact?.name,
    conversation.whatsappContact?.phone,
  ]
    .filter(Boolean)
    .map((value) => String(value).toLowerCase());

  return haystacks.some((value) => value.includes(query));
};

const matchesGlobalFilters = (conversation: Conversation, filters: GlobalFilters) => {
  if (filters.chatBotId && conversation.chatBotId !== filters.chatBotId) {
    return false;
  }

  const statuses = parseFilterValues(filters.status);
  if (statuses.length > 0 && !statuses.includes('all') && !statuses.includes(conversation.status)) {
    return false;
  }

  if (
    filters.conversationType !== undefined &&
    filters.conversationType !== 'all' &&
    conversation.type !== filters.conversationType
  ) {
    return false;
  }

  const sources = parseFilterValues(filters.source);
  if (sources.length > 0) {
    const conversationSource =
      conversation.metadata?.leadSourceType ||
      conversation.metadata?.leadSource ||
      conversation.whatsappContact?.source;
    const normalizedConversationSource =
      conversationSource === 'import' ? 'imported' : conversationSource;

    if (!normalizedConversationSource || !sources.includes(normalizedConversationSource)) {
      return false;
    }
  }

  if (filters.readState === 'read' && (conversation.unreadCount || 0) > 0) {
    return false;
  }

  if (filters.readState === 'unread' && (conversation.unreadCount || 0) === 0) {
    return false;
  }

  const agentIds = parseFilterValues(filters.agentId);
  if (agentIds.length > 0) {
    const matchesAgent = agentIds.some((agentId) =>
      agentId === 'unassigned'
        ? !conversation.assignedAgentId
        : conversation.assignedAgentId === agentId
    );

    if (!matchesAgent) {
      return false;
    }
  }

  const labelIds = parseFilterValues(filters.labelIds);
  if (labelIds.length > 0) {
    const conversationLabelIds = new Set((conversation.labels || []).map((label) => label._id));
    const matchesLabels = labelIds.some((labelId) => conversationLabelIds.has(labelId));
    if (!matchesLabels) {
      return false;
    }
  }

  return matchesGlobalSearch(conversation, filters.search);
};


interface LiveChatStore {
  // State
  conversations: Conversation[];
  statusCounts: StatusCounts;
  activeConversationId: string | null;
  messages: Record<string, Message[]>;
  messagesPagination: Record<string, { hasMore: boolean; isLoading: boolean; isLoadingMore: boolean; offset: number }>;
  isLoading: boolean;
  isSending: boolean;
  filters: ConversationFilters;
  chatBotId: string | null;
  conversationType: number | undefined; // 1 for web, 2 for WhatsApp, undefined for both
  conversationLabels: Record<string, ConversationLabel[]>;
  replyingToMessage: Message | null;

  // Agents state
  agents: AgentOption[];
  agentsLoading: boolean;

  // Global panel state
  isGlobalPanelOpen: boolean;
  globalConversations: Conversation[];
  globalStatusCounts: StatusCounts;
  globalChatbots: ChatbotInfo[];
  globalFilters: GlobalFilters;
  globalIsLoading: boolean;
  globalIsLoadingMore: boolean;
  globalPagination: {
    page: number;
    perPage: number;
    total: number;
    totalPages: number;
    hasMore: boolean;
  };
  totalUnreadCount: number;

  // Actions
  setChatBotId: (id: string) => void;
  setConversationType: (type: number | undefined) => void;
  setFilters: (filters: ConversationFilters) => void;
  setActiveConversation: (id: string | null) => void;
  fetchConversations: (chatBotId: string) => Promise<void>;
  startWebChat: (chatBotId: string, visitorId: string) => Promise<Conversation>;
  fetchMessages: (conversationId: string) => Promise<void>;
  loadMoreMessages: (conversationId: string) => Promise<void>;
  sendMessage: (conversationId: string, content: string) => Promise<void>;
  sendMediaMessage: (conversationId: string, caption: string, file: File) => Promise<void>;
  setReplyingToMessage: (message: Message | null) => void;
  markAsRead: (conversationId: string) => Promise<void>;
  resumeBot: (conversationId: string) => Promise<void>;
  requestAgent: (conversationId: string) => Promise<void>;
  startIntervention: (conversationId: string) => Promise<void>;
  addMessage: (conversationId: string, message: Message) => void;
  updateMessageStatus: (conversationId: string, messageId: string, status: Message['status']) => void;
  updateMessage: (conversationId: string, message: Message) => void;
  updateConversationStatus: (conversationId: string, status: Conversation['status']) => void;
  fetchAgents: (chatBotId: string) => Promise<void>;
  fetchConversationLabels: (chatBotId: string, force?: boolean) => Promise<void>;
  createConversationLabel: (chatBotId: string, name: string, color?: string) => Promise<ConversationLabel>;
  deleteConversationLabel: (chatBotId: string, labelId: string) => Promise<void>;
  updateConversationLabels: (conversationId: string, labelIds: string[]) => Promise<Conversation | null>;
  assignAgent: (conversationId: string, agentId: string | null) => Promise<void>;
  updateAgentStatus: (agentId: string, status: AgentOption['status']) => void;
  clearChat: (conversationId: string) => Promise<void>;
  
  // Global panel actions
  setGlobalPanelOpen: (open: boolean) => void;
  setGlobalFilters: (filters: GlobalFilters) => void;
  fetchGlobalConversations: (options?: { reset?: boolean; search?: string; perPage?: number }) => Promise<void>;
  loadMoreGlobalConversations: (options?: { search?: string }) => Promise<void>;
  updateGlobalConversationStatus: (conversationId: string, status: Conversation['status'], counts?: StatusCounts) => void;
  addOrUpdateGlobalConversation: (conversation: any, counts?: StatusCounts) => void;
  removeConversationFromLists: (conversationId: string) => void;
}

export const useLiveChatStore = create<LiveChatStore>((set, get) => ({
  // Initial state
  conversations: [],
  statusCounts: { all: 0, botActive: 0, pendingAgent: 0, agentActive: 0 },
  activeConversationId: null,
  messages: {},
  messagesPagination: {},
  isLoading: false,
  isSending: false,
  filters: { status: 'all' },
  chatBotId: null,
  conversationType: undefined,
  conversationLabels: {},
  replyingToMessage: null,

  // Agents initial state
  agents: [],
  agentsLoading: false,

  // Global panel initial state
  isGlobalPanelOpen: false,
  globalConversations: [],
  globalStatusCounts: { all: 0, botActive: 0, pendingAgent: 0, agentActive: 0 },
  globalChatbots: [],
  globalFilters: { status: 'all', conversationType: 'all', source: undefined, readState: 'all' },
  globalIsLoading: false,
  globalIsLoadingMore: false,
  globalPagination: {
    page: 0,
    perPage: 30,
    total: 0,
    totalPages: 0,
    hasMore: false,
  },
  totalUnreadCount: 0,
  
  // Actions
  updateMessageStatus: (conversationId, messageId, status) => {
    set((state) => {
      const messages = state.messages[conversationId] || [];
      const updatedMessages = messages.map((msg) =>
        msg._id === messageId ? { ...msg, status } : msg
      );
      return {
        messages: {
          ...state.messages,
          [conversationId]: updatedMessages,
        },
      };
    });
  },
  updateMessage: (conversationId, message) => {
    set((state) => {
      const messages = state.messages[conversationId] || [];
      const updatedMessages = messages.map((msg) => {
        if (msg._id === message._id) {
          // Preserve populated replyTo if the update payload has string ID
          const replyTo =
            message.replyTo && typeof message.replyTo === 'object'
              ? message.replyTo
              : msg.replyTo && typeof msg.replyTo === 'object'
              ? msg.replyTo
              : message.replyTo;
          return { ...msg, ...message, replyTo };
        }
        return msg;
      });
      const resolvedMessages = resolveMessageReplies(updatedMessages);
      return {
        messages: {
          ...state.messages,
          [conversationId]: resolvedMessages,
        },
      };
    });
  },
  setChatBotId: (id) => set({ chatBotId: id }),
  setReplyingToMessage: (message) => set({ replyingToMessage: message }),

  setConversationType: (type) => set({ conversationType: type }),

  setFilters: (filters) => set({ filters }),

  setActiveConversation: (id) => {
    set((state) => {
      if (!id) return { activeConversationId: id, replyingToMessage: null };

      const conversations = state.conversations.map((conv) =>
        conv._id === id ? { ...conv, unreadCount: 0 } : conv
      );
      const globalConversations = state.globalConversations.map((conv) =>
        conv._id === id ? { ...conv, unreadCount: 0 } : conv
      );

      return {
        activeConversationId: id,
        conversations,
        globalConversations,
        totalUnreadCount: globalConversations.reduce((sum, conv) => sum + (conv.unreadCount || 0), 0),
        replyingToMessage: null,
      };
    });
    if (id) {
      get().fetchMessages(id);
      get().markAsRead(id);
    }
  },

  fetchConversations: async (chatBotId) => {
    set({ isLoading: true });
    try {
      const { filters, conversationType } = get();
      const response = await liveChatService.getConversations(
        chatBotId,
        filters.status,
        conversationType,
        filters.labelIds
      );

      const conversations = response.data.conversations.map((conv: any) => ({
        ...conv,
        lastMessageAt: new Date(conv.lastMessageAt),
        createdAt: new Date(conv.createdAt),
        updatedAt: new Date(conv.updatedAt),
      }));

      const statusCounts: StatusCounts = response.data.counts
        ? {
            all: response.data.counts.all,
            botActive: response.data.counts.botActive ?? response.data.counts.bot_active ?? 0,
            pendingAgent: response.data.counts.pendingAgent ?? response.data.counts.pending_agent ?? 0,
            agentActive: response.data.counts.agentActive ?? response.data.counts.agent_active ?? 0,
          }
        : { all: 0, botActive: 0, pendingAgent: 0, agentActive: 0 };

      set({ conversations, statusCounts, isLoading: false });
    } catch (error) {
      console.error('Error fetching conversations:', error);
      set({ isLoading: false });
    }
  },

  startWebChat: async (chatBotId, visitorId) => {
    try {
      const response = await liveChatService.startWebChat(chatBotId, visitorId);
      const conversation = {
        ...response.data.conversation,
        lastMessageAt: new Date(response.data.conversation.lastMessageAt),
        createdAt: new Date(response.data.conversation.createdAt),
        updatedAt: new Date(response.data.conversation.updatedAt),
      };

      // Add to conversations list if not already present
      set((state) => {
        const exists = state.conversations.some(c => c._id === conversation._id);
        if (!exists) {
          return {
            conversations: [conversation, ...state.conversations],
          };
        }
        return state;
      });

      return conversation;
    } catch (error) {
      console.error('Error starting web chat:', error);
      throw error;
    }
  },

  fetchMessages: async (conversationId) => {
    // Set loading state
    set((state) => ({
      messagesPagination: {
        ...state.messagesPagination,
        [conversationId]: {
          ...(state.messagesPagination[conversationId] || { hasMore: false, offset: 0, isLoadingMore: false, isLoading: false }),
          isLoading: true,
        },
      },
    }));

    try {
      const limit = 20;
      const response = await liveChatService.getMessages(conversationId, limit, 0);
      const messages = response.data.map((msg: any) => ({
        ...msg,
        createdAt: new Date(msg.createdAt),
      }));
      const resolvedMessages = resolveMessageReplies(messages);

      set((state) => ({
        messages: {
          ...state.messages,
          [conversationId]: resolvedMessages,
        },
        messagesPagination: {
          ...state.messagesPagination,
          [conversationId]: {
            hasMore: messages.length === limit,
            isLoading: false,
            isLoadingMore: false,
            offset: messages.length,
          },
        },
      }));
    } catch (error) {
      console.error('Error fetching messages:', error);
      // Reset loading state on error
      set((state) => ({
        messagesPagination: {
          ...state.messagesPagination,
          [conversationId]: {
            ...(state.messagesPagination[conversationId] || { hasMore: false, offset: 0, isLoadingMore: false, isLoading: false }),
            isLoading: false,
          },
        },
      }));
    }
  },

  loadMoreMessages: async (conversationId) => {
    const state = get();
    const pagination = state.messagesPagination[conversationId];

    if (!pagination || !pagination.hasMore || pagination.isLoadingMore) {
      return;
    }

    // Set loading state
    set((state) => ({
      messagesPagination: {
        ...state.messagesPagination,
        [conversationId]: {
          ...state.messagesPagination[conversationId],
          isLoadingMore: true,
        },
      },
    }));

    try {
      const limit = 50;
      const response = await liveChatService.getMessages(
        conversationId,
        limit,
        pagination.offset
      );

      const newMessages = response.data.map((msg: any) => ({
        ...msg,
        createdAt: new Date(msg.createdAt),
      }));

      set((state) => {
        const existingMessages = state.messages[conversationId] || [];

        // Filter out any duplicates
        const uniqueNewMessages = newMessages.filter(
          (newMsg: Message) => !existingMessages.some(m => m._id === newMsg._id)
        );

        const allMessages = [...uniqueNewMessages, ...existingMessages];
        const resolvedMessages = resolveMessageReplies(allMessages);

        return {
          messages: {
            ...state.messages,
            [conversationId]: resolvedMessages, // Prepend older messages
          },
          messagesPagination: {
            ...state.messagesPagination,
            [conversationId]: {
              hasMore: newMessages.length === limit,
              isLoading: false,
              isLoadingMore: false,
              offset: pagination.offset + uniqueNewMessages.length,
            },
          },
        };
      });
    } catch (error) {
      console.error('Error loading more messages:', error);
      set((state) => ({
        messagesPagination: {
          ...state.messagesPagination,
          [conversationId]: {
            ...state.messagesPagination[conversationId],
            isLoadingMore: false,
          },
        },
      }));
    }
  },

  sendMessage: async (conversationId, content) => {
    if (!content.trim()) return;

    set({ isSending: true });
    try {
      const state = get();
      const replyToMsg = state.replyingToMessage;
      const response = await liveChatService.sendMessage(
        conversationId,
        content,
        undefined,
        undefined,
        undefined,
        replyToMsg?._id
      );

      // Find chatBotId from either per-chatbot or global conversations
      const conv =
        state.conversations.find((c) => c._id === conversationId) ||
        state.globalConversations.find((c) => c._id === conversationId);

      // Add message to local state
      const newMessage: Message = {
        _id: response.data.message._id,
        conversationId,
        chatBotId: conv?.chatBotId || state.chatBotId || '',
        content,
        isFromUser: false,
        direction: 'outbound',
        sender: 'agent',
        messageType: 'text',
        status: 'sent',
        replyTo: replyToMsg || undefined,
        createdAt: new Date(),
      };

      get().addMessage(conversationId, newMessage);
      set({ isSending: false, replyingToMessage: null });
    } catch (error) {
      console.error('Error sending message:', error);
      set({ isSending: false });
    }
  },

  sendMediaMessage: async (conversationId, caption, file) => {
    set({ isSending: true });
    try {
      const state = get();
      const replyToMsg = state.replyingToMessage;
      
      // Find chatBotId from either per-chatbot or global conversations
      const conv =
        state.conversations.find((c) => c._id === conversationId) ||
        state.globalConversations.find((c) => c._id === conversationId);
      const chatBotId = conv?.chatBotId || state.chatBotId || '';

      const response = await liveChatService.sendMediaMessage(conversationId, caption, file, replyToMsg?._id, chatBotId);

      // Determine message type from file type
      let messageType: Message['messageType'] = 'document';
      if (file.type.startsWith('image/')) messageType = 'image';
      else if (file.type.startsWith('video/')) messageType = 'video';
      else if (file.type.startsWith('audio/')) messageType = 'audio';

      // Add message to local state
      const newMessage: Message = {
        _id: response.data.message._id,
        conversationId,
        chatBotId: conv?.chatBotId || state.chatBotId || '',
        content: caption,
        isFromUser: false,
        direction: 'outbound',
        sender: 'agent',
        messageType,
        mediaUrl: response.data.message.mediaUrl,
        status: 'sent',
        replyTo: replyToMsg || undefined,
        createdAt: new Date(),
      };

      get().addMessage(conversationId, newMessage);
      set({ isSending: false, replyingToMessage: null });
    } catch (error) {
      console.error('Error sending media message:', error);
      set({ isSending: false });
      throw error;
    }
  },

  markAsRead: async (conversationId: string) => {
    try {
      await liveChatService.markAsRead(conversationId);

      // Update local state for both per-chatbot and global conversations
      set((state) => {
        const updatedConversations = state.conversations.map((conv) =>
          conv._id === conversationId ? { ...conv, unreadCount: 0 } : conv
        );
        
        const updatedGlobalConversations = state.globalConversations.map((conv) =>
          conv._id === conversationId ? { ...conv, unreadCount: 0 } : conv
        );
        
        const totalUnreadCount = updatedGlobalConversations.reduce(
          (sum, c) => sum + (c.unreadCount || 0),
          0
        );

        return {
          conversations: updatedConversations,
          globalConversations: updatedGlobalConversations,
          totalUnreadCount,
        };
      });
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  },

  clearChat: async (conversationId) => {
    try {
      await liveChatService.clearChat(conversationId);
      set((state) => ({
        messages: {
          ...state.messages,
          [conversationId]: [],
        },
      }));
    } catch (error) {
      console.error('Error clearing chat:', error);
      throw error;
    }
  },

  resumeBot: async (conversationId) => {
    try {
      await liveChatService.resumeBot(conversationId);
      get().updateConversationStatus(conversationId, 'bot_active');
    } catch (error) {
      console.error('Error resuming bot:', error);
    }
  },


  requestAgent: async (conversationId) => {
    try {
      await liveChatService.requestAgent(conversationId);
      get().updateConversationStatus(conversationId, 'pending_agent');
    } catch (error) {
      console.error('Error requesting agent:', error);
    }
  },

  startIntervention: async (conversationId) => {
    try {
      await liveChatService.updateStatus(conversationId, 'agent_active');
      // Update both per-chatbot and global conversation lists
      set((state) => {
        const updateConv = (conv: Conversation): Conversation =>
          conv._id === conversationId ? { ...conv, status: 'agent_active' } : conv;

        const updatedConversations = state.conversations.map(updateConv);
        const updatedGlobalConversations = state.globalConversations.map(updateConv);

        const statusCounts = {
          all: updatedConversations.length,
          botActive: updatedConversations.filter((c) => c.status === 'bot_active').length,
          pendingAgent: updatedConversations.filter((c) => c.status === 'pending_agent').length,
          agentActive: updatedConversations.filter((c) => c.status === 'agent_active').length,
        };

        return {
          conversations: updatedConversations,
          globalConversations: updatedGlobalConversations,
          statusCounts,
          // Only shift the tab if the user is on a specific status tab (not "All")
          // so the conversation stays visible after status changes
          ...(state.globalFilters.status && state.globalFilters.status !== 'all'
            ? { globalFilters: { ...state.globalFilters, status: 'agent_active' } }
            : {}),
        };
      });
    } catch (error) {
      console.error('Error starting intervention:', error);
      throw error;
    }
  },

  

  addMessage: (conversationId, message) => {
  set((state) => {
    const existingMessages = state.messages[conversationId] || [];
    
    // Check if message already exists by ID
    const messageExists = existingMessages.some(m => m._id === message._id);
    if (messageExists) {
      console.log('⚠️ Message already exists, skipping duplicate:', message._id);
      return state;
    }

    // Add new message and sort by createdAt
    const allMessages = [...existingMessages, message];
    allMessages.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

    const messagePreview = (() => {
      const trimmed = message.content?.trim();
      if (trimmed) return trimmed;

      switch (message.messageType) {
        case 'image':
          return 'Image';
        case 'video':
          return 'Video';
        case 'audio':
          return 'Audio';
        case 'document':
          return 'Document';
        case 'button':
          return 'Button message';
        case 'list':
          return 'List message';
        case 'template':
          return 'Template message';
        case 'workflow':
          return 'Workflow message';
        default:
          return 'New message';
      }
    })();

    const isIncomingUnread =
      message.sender === 'user' &&
      state.activeConversationId !== conversationId;

    const updateConversation = (conv: Conversation): Conversation =>
      conv._id === conversationId
        ? {
            ...conv,
            lastMessageAt: new Date(message.createdAt),
            unreadCount: isIncomingUnread ? (conv.unreadCount || 0) + 1 : conv.unreadCount || 0,
            messageCount: (conv.messageCount || 0) + 1,
            metadata: {
              ...conv.metadata,
              lastMessagePreview: messagePreview,
            },
          }
        : conv;

    const globalConversations = state.globalConversations
      .map(updateConversation)
      .sort((a, b) => new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime());

    const conversations = state.conversations
      .map(updateConversation)
      .sort((a, b) => new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime());

    const resolvedMessages = resolveMessageReplies(allMessages);

    return {
      messages: {
        ...state.messages,
        [conversationId]: resolvedMessages,
      },
      conversations,
      globalConversations,
      totalUnreadCount: globalConversations.reduce((sum, item) => sum + (item.unreadCount || 0), 0),
    };
  });
},

  updateConversationStatus: (conversationId, status) => {
    set((state) => {
      // Update the conversation status
      const updatedConversations = state.conversations.map((conv) =>
        conv._id === conversationId
          ? { ...conv, status }
          : conv
      );

      // Recalculate status counts from local data
      const statusCounts: StatusCounts = {
        all: updatedConversations.length,
        botActive: updatedConversations.filter(c => c.status === 'bot_active').length,
        pendingAgent: updatedConversations.filter(c => c.status === 'pending_agent').length,
        agentActive: updatedConversations.filter(c => c.status === 'agent_active').length,
      };

      return {
        conversations: updatedConversations,
        statusCounts,
      };
    });
  },

  fetchAgents: async (chatBotId) => {
    set({ agentsLoading: true });
    try {
      const response = await liveChatService.getAssignableAgents(chatBotId, true);
      set({ agents: response.data || [], agentsLoading: false });
    } catch (error) {
      console.error('Error fetching assignable agents:', error);
      set({ agentsLoading: false });
    }
  },

  fetchConversationLabels: async (chatBotId, force = false) => {
    if (!chatBotId) return;
    const existing = get().conversationLabels[chatBotId];
    if (!force && existing) return;

    try {
      const response = await liveChatService.getConversationLabels(chatBotId);
      set((state) => ({
        conversationLabels: {
          ...state.conversationLabels,
          [chatBotId]: response.data || [],
        },
      }));
    } catch (error) {
      console.error('Error fetching conversation labels:', error);
    }
  },

  createConversationLabel: async (chatBotId, name, color) => {
    const response = await liveChatService.createConversationLabel(chatBotId, name, color);
    const created = response.data;

    set((state) => {
      const existing = state.conversationLabels[chatBotId] || [];
      const next = [...existing.filter((item) => item._id !== created._id), created].sort((a, b) =>
        a.name.localeCompare(b.name)
      );
      return {
        conversationLabels: {
          ...state.conversationLabels,
          [chatBotId]: next,
        },
      };
    });

    return created;
  },

  deleteConversationLabel: async (chatBotId, labelId) => {
    await liveChatService.deleteConversationLabel(labelId);

    set((state) => {
      const nextLabels = (state.conversationLabels[chatBotId] || []).filter((item) => item._id !== labelId);
      const stripLabel = (conv: Conversation): Conversation => ({
        ...conv,
        labels: (conv.labels || []).filter((item) => item._id !== labelId),
      });

      return {
        conversationLabels: {
          ...state.conversationLabels,
          [chatBotId]: nextLabels,
        },
        conversations: state.conversations.map(stripLabel),
        globalConversations: state.globalConversations.map(stripLabel),
      };
    });
  },

  updateConversationLabels: async (conversationId, labelIds) => {
    const response = await liveChatService.updateConversationLabels(conversationId, labelIds);
    const updatedConversation = response.data || null;
    if (!updatedConversation) return null;

    const normalized: Conversation = {
      ...updatedConversation,
      lastMessageAt: new Date(updatedConversation.lastMessageAt),
      createdAt: new Date(updatedConversation.createdAt),
      updatedAt: new Date(updatedConversation.updatedAt),
    };

    set((state) => {
      const replaceConversation = (conv: Conversation): Conversation =>
        conv._id === conversationId ? { ...conv, labels: normalized.labels || [] } : conv;

      return {
        conversations: state.conversations.map(replaceConversation),
        globalConversations: state.globalConversations.map(replaceConversation),
      };
    });

    return normalized;
  },

  updateAgentStatus: (agentId, status) => {
    set((state) => ({
      agents: state.agents.map((agent) =>
        agent._id === agentId ? { ...agent, status } : agent
      ),
    }));
  },

  assignAgent: async (conversationId, agentId) => {
    try {
      // Send null explicitly when removing assignment (empty string = remove)
      const agentIdPayload = agentId || null;
      await liveChatService.assignAgent(conversationId, agentIdPayload);

      // Find the agent in the local list to get their name for optimistic update
      const agent = agentIdPayload ? get().agents.find((a) => a._id === agentIdPayload) : null;

      // Optimistic update — immediately reflect the change in the UI
      set((state) => {
        const updateConv = (conv: Conversation): Conversation =>
          conv._id === conversationId
            ? {
                ...conv,
                assignedAgentId: agentIdPayload ?? undefined,
                metadata: {
                  ...conv.metadata,
                  assignedAgentName: agent?.name ?? undefined,
                },
              }
            : conv;

        return {
          conversations: state.conversations.map(updateConv),
          globalConversations: state.globalConversations.map(updateConv),
        };
      });

      // Refetch from server to get accurate, filtered data
      await get().fetchGlobalConversations({ reset: true });
    } catch (error) {
      console.error('Error assigning agent:', error);
      throw error;
    }
  },

  // Global panel actions
  setGlobalPanelOpen: (open) => set({ isGlobalPanelOpen: open }),

  setGlobalFilters: (filters) => set({ globalFilters: filters }),

  fetchGlobalConversations: async (options) => {
    const { reset = true, search, perPage } = options || {};
    const state = get();
    const effectiveSearch = search ?? state.globalFilters.search;

    if (!reset) {
      if (state.globalIsLoading || state.globalIsLoadingMore || !state.globalPagination.hasMore) {
        return;
      }
    }

    const nextPage = reset ? 1 : state.globalPagination.page + 1;
    const pageSize = perPage || state.globalPagination.perPage || 30;

    if (reset) {
      set({ globalIsLoading: true, globalIsLoadingMore: false });
    } else {
      set({ globalIsLoadingMore: true });
    }

    try {
      const { globalFilters } = get();
      const conversationType =
        globalFilters.conversationType && globalFilters.conversationType !== 'all'
          ? globalFilters.conversationType
          : undefined;
      const response = await liveChatService.getGlobalConversations(
        globalFilters.chatBotId,
        globalFilters.status,
        conversationType,
        nextPage,
        pageSize,
        effectiveSearch,
        globalFilters.agentId,
        globalFilters.readState,
        globalFilters.labelIds,
        globalFilters.source
      );
      const incoming: Conversation[] = response.data.conversations.map((conv: any) => ({
        ...conv,
        lastMessageAt: new Date(conv.lastMessageAt),
        createdAt: new Date(conv.createdAt),
        updatedAt: new Date(conv.updatedAt),
      }));

      set((current) => {
        const merged = reset
          ? incoming
          : [
            ...current.globalConversations,
            ...incoming.filter(
              (conv) => !current.globalConversations.some((existing) => existing._id === conv._id)
            ),
          ];

        merged.sort((a, b) => new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime());

        const pagination = response.data.pagination || {};
        const currentPage = pagination.page || nextPage;
        const totalPages = pagination.totalPages || 0;
        const totalUnreadCount =
          response.data.counts?.totalUnreadCount ??
          merged.reduce((sum, c) => sum + (c.unreadCount || 0), 0);

        return {
          globalConversations: merged,
          globalChatbots: response.data.chatbots ,
          globalStatusCounts: response.data.counts ,
          totalUnreadCount,
          globalPagination: {
            page: currentPage,
            perPage: pagination.perPage || pageSize,
            total: pagination.total || 0,
            totalPages,
            hasMore: totalPages > 0 ? currentPage < totalPages : incoming.length === pageSize,
          },
          globalIsLoading: false,
          globalIsLoadingMore: false,
        };
      });
    } catch (error) {
      console.error('Error fetching global conversations:', error);
      set({ globalIsLoading: false, globalIsLoadingMore: false });
    }
  },

  loadMoreGlobalConversations: async (options) => {
    await get().fetchGlobalConversations({
      reset: false,
      search: options?.search,
    });
  },

  updateGlobalConversationStatus: (conversationId, status, counts) => {
    set((state) => {
      const previousConversation = state.globalConversations.find((conv) => conv._id === conversationId);
      if (!previousConversation) {
        // Even if the conversation isn't in the current list, we should update the counts if provided
        if (counts) {
          return {
            globalStatusCounts: counts
          };
        }
        return state;
      }

      const nextConversation = { ...previousConversation, status };
      const updated = matchesGlobalFilters(nextConversation, state.globalFilters)
        ? state.globalConversations.map((conv) =>
            conv._id === conversationId ? nextConversation : conv
          )
        : state.globalConversations.filter((conv) => conv._id !== conversationId);

      return {
        globalConversations: updated,
        totalUnreadCount: updated.reduce((sum, item) => sum + (item.unreadCount || 0), 0),
        globalStatusCounts: counts || state.globalStatusCounts,
      };
    });
  },

  // Directly add or update a conversation in the global list using a WebSocket payload.
  // This gives instant UI feedback without a network round-trip.
  addOrUpdateGlobalConversation: (raw: any, counts?: StatusCounts) => {
    set((state) => {
      const existingConversation = state.globalConversations.find((c) => c._id === raw._id);
      const normalizedLabels =
        Array.isArray(raw.labels) &&
        raw.labels.every((label: any) => label && typeof label === 'object' && '_id' in label)
          ? raw.labels
          : existingConversation?.labels || [];
      const conv: Conversation = {
        ...raw,
        labels: normalizedLabels,
        lastMessageAt: new Date(raw.lastMessageAt),
        createdAt: new Date(raw.createdAt),
        updatedAt: new Date(raw.updatedAt),
      };

      if ((conv.messageCount ?? 0) === 0) {
        if (counts) return { globalStatusCounts: counts };
        return state;
      }

      if (!matchesGlobalFilters(conv, state.globalFilters)) {
        if (!existingConversation) {
          if (counts) return { globalStatusCounts: counts };
          return state;
        }

        const filteredConversations = state.globalConversations.filter((c) => c._id !== conv._id);
        return {
          globalConversations: filteredConversations,
          totalUnreadCount: filteredConversations.reduce((sum, item) => sum + (item.unreadCount || 0), 0),
          globalStatusCounts: counts || state.globalStatusCounts,
        };
      }

      const exists = Boolean(existingConversation);
      let updated: Conversation[];
      if (exists) {
        updated = state.globalConversations.map(c => c._id === conv._id ? conv : c);
      } else {
        updated = [conv, ...state.globalConversations];
      }
      // Keep sorted by lastMessageAt desc
      updated.sort((a, b) => new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime());

      return {
        globalConversations: updated,
        totalUnreadCount: updated.reduce((sum, item) => sum + (item.unreadCount || 0), 0),
        globalStatusCounts: counts || state.globalStatusCounts,
      };
    });
  },

  removeConversationFromLists: (conversationId: string) => {
    set((state) => {
      const globalConversations = state.globalConversations.filter((conv) => conv._id !== conversationId);
      
      return {
        conversations: state.conversations.filter((conv) => conv._id !== conversationId),
        globalConversations,
        totalUnreadCount: globalConversations.reduce((sum, item) => sum + (item.unreadCount || 0), 0),
        // globalStatusCounts: buildGlobalStatusCounts(globalConversations),
        activeConversationId:
          state.activeConversationId === conversationId ? null : state.activeConversationId,
      };
    });
  },
}))
